// Import Control step-5 editor (app.md §9 step 5). The ONLY place invoice/line item
// values are editable. Edits header + line items, resolves flags (auto on save when
// the condition holds; explicit via "Mark resolved"). "Save & Release" persists
// the edits and releases in one click; it lights up once every flag is resolved
// or resolvable, vendor + company are set, and the amounts reconcile.
//
// PORT NOTE — Save is now ONE request. `intakeSave` carries the header fields,
// the line upserts and removals, and (for Save & Release) the release itself.
// The server writes the correction rows, auto-resolves the flags whose condition
// now holds, recomputes the invoice's effective company and its routing, runs the
// release gates and performs the Import Control approve — as one transaction, so
// a release either happens completely or not at all. What used to be here and is
// now gone: the per-field PATCH sequence, the correction diff against a rebased
// baseline, the flag auto-resolution writes, the unresolved-count write, and the
// separate release decision. The arithmetic checks stay client-side for instant
// feedback; the server enforces them independently and is the authority.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  Body1,
  Button,
  Caption1,
  Divider,
  Dropdown,
  Field,
  Input,
  Option,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Subtitle2,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { AddRegular, DeleteRegular, DismissRegular, InfoRegular, LinkRegular } from '@fluentui/react-icons'
import type { InvoiceLineItem, InvoiceLineItemWrite, Flag, Invoice, IntakeSaveRequest } from '../lib/types'
import { checkAmounts, formatNumericDate, toNumber } from '../lib/format'
import {
  DecisionStep,
  DecisionOutcome,
  InvoiceStatus,
  PCS_COMPANY_ID,
  PCS_COMPANY_NAME,
  flagLabel,
  flagResolution,
  flagDescription,
  accountSourceLabel,
  type ReasonCategory,
} from '../lib/constants'
import {
  createDecision,
  intakeSave,
  resolveFlag,
  searchExpenseTypes,
  searchCompanies,
  searchVendors,
} from '../lib/data'
import { errorMessage, isApiError } from '../api/errors'
import { useHalfLabels } from '../lib/hooks'
import { EntityPicker, type EntityOption } from './EntityPicker'
import { AssociatedEntitiesEditor } from './AssociatedEntitiesEditor'
import { NotAnInvoiceDialog } from './NotAnInvoiceDialog'
import { AccountsSummary } from './AccountsSummary'

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '16px' },
  // Per-line action cell: info popover trigger + remove button.
  rowActions: { display: 'flex', alignItems: 'center', gap: '2px', justifyContent: 'flex-end' },
  // Popover body listing where a line's account came from + any codes on the page.
  provenance: { display: 'flex', flexDirection: 'column', gap: '4px', maxWidth: '260px' },
  provenanceHead: { fontWeight: tokens.fontWeightSemibold, marginBottom: '2px' },
  // "Add line item" button row under the line items table.
  addRow: { display: 'flex', paddingTop: '4px' },
  // Save + Release pinned to the top of the scrolling modal body, always visible.
  actionBar: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 0',
    backgroundColor: tokens.colorNeutralBackground1,
    // Blue rule under the modal's header band (mirrors the page's blue line).
    borderBottom: `3px solid ${tokens.colorBrandStroke1}`,
  },
  actionStatus: { flex: 1, minWidth: 0, fontWeight: tokens.fontWeightSemibold },
  actionOk: { color: tokens.colorPaletteGreenForeground1 },
  actionBlocked: { color: tokens.colorNeutralForeground1 },
  // PDF + invoice header side-by-side on top; line items span full width below.
  topRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    '@media (max-width: 900px)': { gridTemplateColumns: '1fr' },
  },
  pdfWrap: { height: '440px', minHeight: 0, minWidth: 0, '@media (max-width: 900px)': { height: '300px' } },
  headerCol: { display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' },
  // Fields that sit alone on their row span both columns (no orphan gap).
  fullRow: { gridColumn: '1 / -1' },
  // AI extraction/matching notes — plain muted text under the summary fields.
  aiNotes: {
    gridColumn: '1 / -1',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    color: tokens.colorNeutralForeground3,
  },
  // Flags render as stacked MessageBars (warning = blocks release, success = done).
  flagList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  flagNone: { color: tokens.colorNeutralForeground3 },
  // Blue rule closing the Flags section — mirrors the action bar's line above,
  // so the flags read as a distinct band before the invoice detail below.
  blueRule: { borderBottom: `3px solid ${tokens.colorBrandStroke1}` },
  // Every editor fills its cell so the line item rows line up column-to-column.
  cellInput: { width: '100%', minWidth: 0 },
  // Fixed layout: sized utility/amount columns, Description takes the rest.
  // minWidth is the sum of the sized columns (710px) plus a floor for
  // Description, so the field that says what the line item IS never collapses when
  // the modal is narrow — below that the wrapper scrolls instead (QA Q17: at
  // 915px the Description input drew as a hairline and the header labels
  // overprinted each other). Header labels clip with an ellipsis rather than
  // spilling over the next column.
  lineItemsTable: {
    tableLayout: 'fixed',
    width: '100%',
    minWidth: '940px',
    '& .fui-TableHeaderCell__button': {
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  // Let the line items table scroll horizontally rather than overlap when the modal
  // is narrow (the 7 columns have minimum widths that can exceed the width).
  // maxWidth pins the scroller to the modal so the table can't widen the dialog
  // itself (which clipped its own left edge — QA Q17).
  tableScroll: { overflowX: 'auto', maxWidth: '100%', minWidth: 0 },
  // Tint a line item's Account cell when a missing-account flag points at it.
  cellFlagged: { backgroundColor: tokens.colorPaletteRedBackground1, borderRadius: tokens.borderRadiusSmall },
  // Provenance line under the Account picker (source + document GL codes).
  accountMeta: { display: 'block', color: tokens.colorNeutralForeground3, marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  // GL account cell = picker + its info popover trigger, side by side (the
  // provenance "i" now lives beside the field it explains — 2026-07-14).
  accountCell: { display: 'flex', alignItems: 'center', gap: '2px', minWidth: 0 },
  accountPicker: { flex: 1, minWidth: 0 },
  // The per-line entity editor now lives in a popover off the row's tag button
  // (2026-07-14) rather than an always-open row under every line item. Give the
  // surface a comfortable width so its 5-column rows don't crush.
  entityPop: { width: '540px', maxWidth: '82vw' },
  // Mounted inside the Dialog (see the mountNode note), so it shares a stacking
  // context with the sticky action bar (z-index 2) instead of sitting in the
  // body portal above everything. Lift it clear.
  entityPopSurface: { zIndex: 10 },
  // Hint shown for an unsaved line (no real line item id to hang entities on yet).
  entityHint: { display: 'block', padding: '6px 12px', color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  // What is stored right now, shown after a concurrent edit collided with this one.
  conflict: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '8px 12px',
    borderLeft: `3px solid ${tokens.colorPaletteRedBorderActive}`,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
  },
  conflictHead: { fontWeight: tokens.fontWeightSemibold },
})

interface HeaderState {
  externalinvoiceid: string
  date: string
  subtotal: string
  tax: string
  total: string
  currency: string
  vendorId?: string
  vendorName?: string
  companyId?: string
  companyName?: string
  /** The reviewer's REQUEST for manual Point Click Care entry
      (`manual_entry_requested`). The effective routing — the request or a
      cross-company split — is derived by the server and read-only here. Past
      the last staffed approval step a manual-entry invoice goes to Manual Entry
      instead of Export Control (app.md §9 Manual Entry). */
  manualEntry: boolean
}

interface LineItemState {
  lineItemId: string
  /** Concurrency counter of the stored line; absent on a line not saved yet. */
  rowVersion?: number
  sequence?: number | null
  description: string
  quantity: string
  price: string
  total: string
  /**
   * "What is it?" — the expense type. IMPORT CONTROL EDITS THIS HALF AND ONLY
   * THIS HALF. The cost center is not on the document and cannot be answered
   * here, so it is neither shown nor sent; the server refuses it either way.
   */
  expenseTypeCode?: string
  expenseTypeLabel?: string
  /** Provenance of the expense type; 'Manual' once the reviewer changes it. */
  expenseTypeSource?: string
  /** The printed GL code read off the document by pass 1 (read-only context). */
  printedCode?: string
  /** Per-line company (schema.md invoice_line_items). Untouched lines inherit the
      header company on save; a line the reviewer explicitly set (companyTouched,
      or stored differing from the invoice's) keeps its own — that's how one
      invoice splits across companies for the per-company PCC export. */
  companyId?: string
  companyName?: string
  companyTouched?: boolean
  /** True for a line the reviewer added manually and hasn't saved yet — created
      (not updated) on save, then flipped false once it has a real id. */
  isNew?: boolean
}

/** "A", "A and B", "A, B and C" — company names read as prose in the hint. */
function listCompanies(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

interface CompanyResolution {
  companyId?: string
  companyName?: string
  /** 'invoice' — the reviewer's own pick stands; 'lines' — every line points at
      one other company, which wins; 'mixed' — the lines disagree. */
  outcome: 'invoice' | 'lines' | 'mixed'
  /** Distinct company names the lines point at, for the hint text. */
  lineCompanyNames: string[]
}

/** The invoice's company follows its line items (user rule, 2026-07-31):
    unanimous lines set it, and lines that disagree bill it to PACE Consulting
    Solutions — which has staff to hand the approvals to — and route it to Manual
    Entry, because no single per-company export file fits it.

    The API applies this same rule and its answer is the one that gets stored;
    this copy exists so the reviewer can see where the invoice is going BEFORE
    releasing it, and so the Company field can say what set it. */
function resolveInvoiceCompany(
  invoiceCompanyId: string | undefined,
  invoiceCompanyName: string | undefined,
  lines: Array<{ companyId?: string; companyName?: string }>,
): CompanyResolution {
  const names = new Map<string, string>()
  for (const l of lines) {
    const id = l.companyId?.trim()
    if (!id) continue
    if (!names.has(id)) names.set(id, l.companyName?.trim() || id)
  }
  const ids = [...names.keys()]
  const lineCompanyNames = [...names.values()]
  const own: CompanyResolution = {
    companyId: invoiceCompanyId,
    companyName: invoiceCompanyName,
    outcome: 'invoice',
    lineCompanyNames,
  }
  if (ids.length === 0) return own
  if (ids.length === 1) {
    const id = ids[0]
    if (id === (invoiceCompanyId || '')) return own
    return { companyId: id, companyName: names.get(id), outcome: 'lines', lineCompanyNames }
  }
  return {
    companyId: PCS_COMPANY_ID,
    companyName: PCS_COMPANY_NAME,
    outcome: 'mixed',
    lineCompanyNames,
  }
}

/** Each line's EFFECTIVE company: a line the reviewer pinned uses its own, an
    untouched line follows the invoice's company. Always read from the header's
    OWN company — the value the reviewer picked and the save stores on the
    lines — never from the company the lines resolved to. Feeding the resolved
    company back in would move the untouched lines with it and flip the answer
    on the next pass. */
function effectiveLineCompanies(h: HeaderState, rows: LineItemState[]) {
  return rows.map((r) =>
    r.companyTouched
      ? { companyId: r.companyId, companyName: r.companyName }
      : { companyId: h.companyId, companyName: h.companyName },
  )
}

/** `companyId` is the invoice's company AFTER the lines have had their say
    (resolveInvoiceCompany) — lines that set the company also satisfy
    MissingCompany. */
function isAutoSatisfied(flag: string, h: HeaderState, lineItems: LineItemState[], companyId: string | undefined): boolean {
  switch (flag) {
    case 'MissingTotal':
      return h.total.trim() !== ''
    case 'MissingExternalInvoiceId':
      return h.externalinvoiceid.trim() !== ''
    case 'MissingVendor':
      return !!h.vendorId
    case 'MissingCompany':
      return !!companyId
    case 'MissingExpenseType':
      return lineItems.length > 0 && lineItems.every((c) => !!c.expenseTypeCode)
    case 'LineItemsToTotalMismatch': {
      // Same two-part test as the release gate (v1.34; lines vs. TOTAL since
      // 2026-07-29 — the lines carry the sales tax). No capped exemption: the
      // line items must add up to the total for every invoice.
      const sum = lineItems.reduce((a, c) => a + toNumber(c.total), 0)
      return checkAmounts(sum, h.subtotal, h.tax, h.total).reconciles
    }
    default:
      return false
  }
}

// Format a numeric string to 2 decimal places for money-field display
// (e.g. "3918.6" -> "3918.60"). Blanks and non-numeric text pass through
// unchanged. toNumber() parses these back identically, so no data change.
function money2(v: string): string {
  if (v == null || v.trim() === '') return ''
  const n = Number(v)
  return Number.isNaN(n) ? v : n.toFixed(2)
}

interface Props {
  invoice: Invoice
  lineItems: InvoiceLineItem[]
  flags: Flag[]
  onChanged: () => void
  onReleased: () => void
  /** Called after the record is archived as not an invoice. */
  onArchived?: () => void
  /** Optional PDF preview, shown beside the header (Import Control). */
  pdf?: ReactNode
  /** Close the modal — rendered as an X in the header action bar. */
  onClose?: () => void
}

export function EditableInvoiceForm({ invoice, lineItems, flags, onChanged, onReleased, onArchived, pdf, onClose }: Props) {
  const s = useStyles()
  // The Dialog element this form is rendered inside, used as the mount point for
  // the per-line entity popover.
  //
  // That popover holds real controls (the type dropdown, the source input, the
  // entity picker), and clicking any of them used to close it a beat later.
  // Verified in the browser, not guessed: focus left the control and landed on
  // the comment box 0.1–1s after the click, with tabster's
  // ModalizerAPI._restoreModalizerFocus on the stack. Two causes, both needed
  // fixing — a PopoverSurface portals to document.body, i.e. OUTSIDE the
  // Dialog's focus trap, and `trapFocus` gave it a second, nested trap. Either
  // way focus was sitting somewhere the Dialog's trap considered illegal, so
  // tabster hauled it back. Mounting the surface inside the Dialog and dropping
  // `trapFocus` leaves exactly one trap, with the popover inside it. Same
  // principle as the inlinePopup listboxes on Dropdown/Combobox. The mount point
  // is the Dialog surface, not the scrolling body, so the popover isn't clipped
  // by the scroll container.
  const rootRef = useRef<HTMLDivElement>(null)
  const [dialogEl, setDialogEl] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setDialogEl(rootRef.current?.closest('[role="dialog"]') as HTMLElement | null)
  }, [])
  const [header, setHeader] = useState<HeaderState>(() => ({
    externalinvoiceid: invoice.external_invoice_id ?? '',
    date: invoice.invoice_date ? invoice.invoice_date.slice(0, 10) : '',
    subtotal: invoice.subtotal != null ? money2(String(invoice.subtotal)) : '',
    tax: invoice.tax != null ? money2(String(invoice.tax)) : '',
    total: invoice.total != null ? money2(String(invoice.total)) : '',
    currency: invoice.currency ?? 'USD',
    vendorId: invoice.vendor_id || undefined,
    vendorName: invoice.vendor_name || undefined,
    companyId: invoice.company_id || undefined,
    companyName: invoice.company_name || undefined,
    manualEntry: invoice.manual_entry_requested ?? false,
  }))
  const [rows, setRows] = useState<LineItemState[]>(() =>
    lineItems.map((c) => ({
      lineItemId: c.id,
      rowVersion: c.row_version,
      sequence: c.line_number,
      description: c.description ?? '',
      quantity: c.quantity != null ? String(c.quantity) : '',
      price: c.unit_price != null ? money2(String(c.unit_price)) : '',
      total: c.total != null ? money2(String(c.total)) : '',
      expenseTypeCode: c.expense_type_code || undefined,
      expenseTypeSource: c.expense_type_source || undefined,
      printedCode: c.printed_account_code || undefined,
      companyId: c.company_id || undefined,
      companyName: c.company_name || undefined,
      // A stored company differing from the invoice's was set deliberately
      // (a cross-company split) — keep it pinned; matching-stamped copies of
      // the invoice company stay inherited and follow header edits.
      companyTouched: !!c.company_id && c.company_id !== (invoice.company_id || undefined),
    })),
  )
  // The stored invoice's concurrency counter. Sent with every save and replaced
  // from each response, so two people editing the same invoice can't silently
  // overwrite each other: the second save comes back 409 and we re-present what
  // is actually stored. (This replaced a rebased copy of the whole invoice + line
  // set, which the old client kept only to work out which fields to PATCH.)
  const [rowVersion, setRowVersion] = useState<number>(invoice.row_version)
  const [serverRow, setServerRow] = useState<Invoice | null>(null) // set on a version conflict
  const [removedIds, setRemovedIds] = useState<string[]>([]) // existing line item rows the reviewer removed
  const newIdRef = useRef(0) // counter for temp ids on unsaved added lines
  const [saving, setSaving] = useState(false)
  const [releasing, setReleasing] = useState(false)
  const [notInvoiceOpen, setNotInvoiceOpen] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [dirty, setDirty] = useState(false) // unsaved header/line item edits
  const [resolving, setResolving] = useState(false) // a Mark-resolved write in flight
  const [error, setError] = useState<string | null>(null)
  // Edit counter, bumped by every mutator. A save persists the snapshot it
  // started from; anything typed while it was in flight leaves this higher than
  // the value captured at the start, so the save must NOT clear the dirty flag
  // and a release must not go out against the pre-edit values (QA Q11).
  const editSeq = useRef(0)

  const unresolvedFlags = useMemo(
    () => new Set(flags.filter((f) => !f.resolved_at).map((f) => f.flag_type)),
    [flags],
  )

  // Warm the shared empty-term results cache on open (EntityPicker keys it by
  // the search function), so the first Account picker opened on a many-line
  // invoice has its "top results" already in hand. That cache is a PAGE of
  // results, not the master, which is why the label lookup below is separate.
  useEffect(() => {
    void searchExpenseTypes('')
  }, [])

  // Code -> label for the expense types already on the loaded lines. See
  // `useHalfLabels`; the codes are what the wire carries.
  const labels = useHalfLabels()

  // Loaded line items keyed by id — lets the per-line entity editor (AE4) attach to
  // the real line item row. A line added and saved this session isn't in the loaded
  // set (line items reload is deferred), so the editor synthesizes a stub carrying
  // just the real id it needs.
  const lineItemsById = useMemo(() => new Map(lineItems.map((c) => [c.id, c])), [lineItems])

  // Arithmetic that gates release: the line items sum to the total (they carry
  // the sales tax), and the printed subtotal + tax = total. checkAmounts() in
  // lib/format.ts owns the rule and explains why.
  const lineItemsSum = useMemo(() => rows.reduce((a, c) => a + toNumber(c.total), 0), [rows])
  const { lineItemsMatchTotal, headerAddsUp, reconciles: amountsReconcile } = checkAmounts(
    lineItemsSum,
    header.subtotal,
    header.tax,
    header.total,
  )

  // Capped extraction. Extraction keeps only the first 40 line items and marks
  // the invoice, so the stored lines fall short of the total until a reviewer
  // fixes them. Matching suppresses LineItemsToTotalMismatch on these (flags.md →
  // MaximumLineItemsExtracted) because MaximumLineItemsExtracted already says
  // "verify against the source" and the reconcile flag would misdirect to
  // "correct the amounts". This is used ONLY to word the release hint. There is
  // no exemption from the reconcile rule: the line items must add up to the total
  // for every invoice, and rolling the lines up against the source is the review
  // work the flag is asking for (user, 2026-07-31).
  const lineItemsCapped = !!invoice.line_items_capped

  // Where this invoice is heading, derived live so the reviewer sees it before
  // releasing (resolveInvoiceCompany above). The server decides for real.
  //
  // The resolved company is NEVER written back into header.companyId: the line
  // list is built from the header's own company, so writing it back would move
  // every untouched line and re-resolve to a different answer each pass.
  const companyResolution = useMemo(
    () => resolveInvoiceCompany(header.companyId, header.companyName, effectiveLineCompanies(header, rows)),
    [rows, header],
  )
  // The company the invoice is released and stored under.
  const companyId = companyResolution.companyId
  const companyName = companyResolution.companyName
  const linesSetCompany = companyResolution.outcome !== 'invoice'
  // What the save must store: the reviewer's own Manual Entry pick, or a
  // mixed-company invoice, which has no CSV path.
  const manualEntryEffective = header.manualEntry || companyResolution.outcome === 'mixed'
  const pathLocked = companyResolution.outcome === 'mixed'

  // Flags that would still block after a save: explicit ones not yet marked
  // resolved, and auto ones whose condition doesn't hold yet. Auto flags whose
  // condition holds don't block — Save & Release resolves them on its save.
  const blockingFlags = flags.filter(
    (f) =>
      !f.resolved_at &&
      !(flagResolution(f.flag_type) === 'auto' && f.flag_type && isAutoSatisfied(f.flag_type, header, rows, companyId)),
  ).length

  // Every line needs an EXPENSE TYPE (QA Q12). NOT a cost center: that half is
  // settled at Department Approval or later by someone who knows which
  // discipline the spend was for, and gating release on it here would make a
  // rubber stamp mandatory on the 73% of the chart whose expense types admit
  // more than one cost center. This mirrors the server's third release gate
  // exactly — `line_item_without_expense_type`.
  //
  // The `MissingExpenseType` flag only exists when Matching raised one, so a
  // line the reviewer ADDS here carries no flag and used to sail through — and
  // then aborted the whole Export Control batch, four steps later.
  const linesWithoutExpenseType = rows.filter((r) => !r.expenseTypeCode)


  // At least one active line is required to release: with zero lines both
  // reconcile checks pass trivially (toNumber('') === 0), so an invoice whose
  // every line item was removed could otherwise release with no amounts at all.
  const canSaveAndRelease =
    blockingFlags === 0 &&
    rows.length > 0 &&
    linesWithoutExpenseType.length === 0 &&
    !!header.vendorId &&
    !!companyId &&
    amountsReconcile

  // Field-level highlight: which fields are driving an unresolved flag. For the
  // auto-clearing flags, drop the highlight the moment the value is fixed (before
  // save) so a corrected field doesn't keep showing red; low-confidence flags need
  // an explicit resolve, so they stay highlighted until then.
  const has = (f: string) => unresolvedFlags.has(f)
  const vendorErr = (has('MissingVendor') && !header.vendorId) || has('VendorMatchLowConfidence')
  const companyErr = (has('MissingCompany') && !companyId) || has('CompanyMatchLowConfidence')
  const extIdErr = has('MissingExternalInvoiceId') && !header.externalinvoiceid.trim()
  const totalErr = (has('MissingTotal') && !header.total.trim()) || (has('LineItemsToTotalMismatch') && !amountsReconcile)
  const currencyErr = has('NonUSDCurrency') && header.currency.trim().toUpperCase() !== 'USD'
  // No MissingAccount highlight variable: the Account cell is tinted from the
  // line itself now (QA Q12), which covers flagged and reviewer-added lines alike.

  // Every mutator goes through this: flags the form dirty and bumps the edit
  // counter a save in flight compares itself against (QA Q11).
  function touched() {
    editSeq.current += 1
    setDirty(true)
  }

  function setH<K extends keyof HeaderState>(key: K, value: HeaderState[K]) {
    touched()
    setHeader((prev) => ({ ...prev, [key]: value }))
  }

  function patchHeader(patch: Partial<HeaderState>) {
    touched()
    setHeader((prev) => ({ ...prev, ...patch }))
  }

  function setRow(id: string, patch: Partial<LineItemState>) {
    touched()
    setRows((prev) => prev.map((r) => (r.lineItemId === id ? { ...r, ...patch } : r)))
  }

  function addRow() {
    touched()
    newIdRef.current += 1
    setRows((prev) => {
      const maxSeq = prev.reduce((m, r) => Math.max(m, r.sequence ?? 0), 0)
      return [
        ...prev,
        {
          lineItemId: `new-${newIdRef.current}`,
          sequence: maxSeq + 1,
          description: '',
          quantity: '',
          price: '',
          total: '',
          // No expenseTypeSource until one is actually picked (QA Q12): the
          // picker stamps 'Manual' on selection, and stamping it up front
          // labelled an empty value as a human choice in the feedback metrics.
          isNew: true,
        },
      ]
    })
  }

  function removeRow(id: string) {
    touched()
    const row = rows.find((r) => r.lineItemId === id)
    if (row && !row.isNew) setRemovedIds((ids) => [...ids, id]) // deactivate existing rows on save
    setRows((prev) => prev.filter((r) => r.lineItemId !== id))
  }

  // Save. One request carries the header, the line upserts, the removals and —
  // when `release` is set — the release itself, applied as a single transaction.
  //
  // Returns whether the release went through AND whether the reviewer edited
  // anything while the request was in flight (QA Q11). The save persists the
  // snapshot it started from — that is unavoidable, the round trip takes as long
  // as it takes — so what matters is that the form does not then LIE about it: it
  // stays dirty, Save stays enabled, and a release is refused rather than sending
  // the pre-edit values on to the approvers.
  async function persistAll(release: boolean): Promise<{ released: boolean; raced: boolean }> {
    const seqAtStart = editSeq.current
    // Freeze exactly what this save writes, so every part of it reads one
    // consistent set of values.
    const savedHeader = header
    const savedRows = rows
    const savedRemoved = removedIds
    // A blank field CLEARS the stored column: the request is PATCH-shaped, where
    // an absent key means "leave alone" and null means "clear". Sending null for
    // a field that was already empty is a no-op, so no before/after bookkeeping
    // is needed to tell the two apart.
    const textOrNull = (v: string): string | null => (v.trim() !== '' ? v.trim() : null)
    const numOrNull = (v: string): number | null => (v.trim() !== '' ? toNumber(v) : null)

    // An edit typed while the request is in flight is not in what was stored, so
    // a release now would send the pre-edit values on (QA Q11). Ask for the
    // release only if nothing has changed since this snapshot was taken.
    const raced = () => editSeq.current !== seqAtStart

    const upsert: InvoiceLineItemWrite[] = savedRows.map((r) => ({
      // No id = create. With an id the line's own concurrency counter rides
      // along, so an edit to a line someone else just changed is refused rather
      // than silently winning.
      ...(r.isNew ? {} : { id: r.lineItemId, row_version: r.rowVersion }),
      line_number: r.sequence ?? null,
      description: textOrNull(r.description),
      // THE EXPENSE TYPE ONLY. `cost_center_code` is deliberately not sent:
      // under the absent-vs-null convention sending it null would CLEAR a
      // `ChartImplied` cost center on every Import Control save.
      expense_type_code: r.expenseTypeCode ?? null,
      // Materialize the line's company: explicitly-set lines keep their own,
      // everything else inherits the header's OWN company (this is what splits
      // the export into one file per company — export.md). Not the company the
      // lines resolved to: an untouched line's cost belongs to the company the
      // reviewer billed the invoice to, and storing the resolved value would
      // re-point every line each time the answer moved.
      company_id: (r.companyTouched ? r.companyId : savedHeader.companyId) ?? null,
      company_name: (r.companyTouched ? r.companyName : savedHeader.companyName) ?? null,
      quantity: numOrNull(r.quantity),
      unit_price: numOrNull(r.price),
      total: numOrNull(r.total),
    }))

    const body: IntakeSaveRequest = {
      row_version: rowVersion,
      invoice: {
        external_invoice_id: textOrNull(savedHeader.externalinvoiceid),
        invoice_date: textOrNull(savedHeader.date),
        subtotal: numOrNull(savedHeader.subtotal),
        tax: numOrNull(savedHeader.tax),
        total: numOrNull(savedHeader.total),
        currency: textOrNull(savedHeader.currency),
        vendor_id: savedHeader.vendorId ?? null,
        vendor_name: savedHeader.vendorName ?? null,
        // The reviewer's own company pick. The server re-derives the invoice's
        // effective company from the lines, exactly as the hint above predicts.
        company_id: savedHeader.companyId ?? null,
        company_name: savedHeader.companyName ?? null,
        // The REQUEST only. A cross-company split adds itself to this
        // server-side; `manual_entry` is derived and never written from here.
        manual_entry_requested: savedHeader.manualEntry,
      },
      line_items: { upsert, remove: savedRemoved },
      release: release && !raced(),
    }

    const result = await intakeSave(invoice.id, body)
    const releaseRequested = body.release === true
    // Reseed from what the server stored — the response is the record. Skip the
    // reseed when the reviewer typed during the round trip: their unsaved values
    // must survive, and the form stays dirty to say so.
    const stillClean = !raced()
    setRowVersion(result.invoice.row_version)
    setServerRow(null)
    if (stillClean) {
      const saved = result.invoice
      setHeader({
        externalinvoiceid: saved.external_invoice_id ?? '',
        date: saved.invoice_date ? saved.invoice_date.slice(0, 10) : '',
        subtotal: saved.subtotal != null ? money2(String(saved.subtotal)) : '',
        tax: saved.tax != null ? money2(String(saved.tax)) : '',
        total: saved.total != null ? money2(String(saved.total)) : '',
        currency: saved.currency ?? 'USD',
        vendorId: saved.vendor_id || undefined,
        vendorName: saved.vendor_name || undefined,
        // Keep the reviewer's own pick, not the resolved company: the lines are
        // built from it, so adopting the resolved value would move every
        // untouched line on the next save.
        companyId: savedHeader.companyId,
        companyName: savedHeader.companyName,
        manualEntry: saved.manual_entry_requested ?? false,
      })
      // Adopt the stored lines: a created line arrives with its real id, and
      // every line with a fresh concurrency counter for the next save.
      if (result.line_items) setRows(result.line_items.map(lineItemToRow))
      setRemovedIds([])
      setDirty(false)
    } else {
      // Keep every value the reviewer typed, but take the identity the server
      // just assigned: a line created by this save now HAS an id, and re-sending
      // it as new would create a second copy of it.
      if (result.line_items) adoptLineIdentity(result.line_items)
      // The removals this save processed are done; anything deleted since is not.
      setRemovedIds((prev) => prev.filter((id) => !savedRemoved.includes(id)))
    }
    return { released: releaseRequested && !!result.released_to, raced: !stillClean }
  }

  /** Take the stored lines' ids and concurrency counters without touching any
      value on screen. Used when a save landed but the reviewer had typed since:
      their text stays, while a line the save created stops being "new". Created
      lines are paired by line number — they come back with the number they were
      sent with — and otherwise in the order they were sent. */
  function adoptLineIdentity(stored: InvoiceLineItem[]) {
    setRows((prev) => {
      const known = new Set(prev.filter((r) => !r.isNew).map((r) => r.lineItemId))
      const pool = stored.filter((c) => !known.has(c.id))
      const take = (sequence: number | null | undefined): InvoiceLineItem | undefined => {
        let at = sequence == null ? -1 : pool.findIndex((c) => c.line_number === sequence)
        if (at < 0) at = 0
        return pool.splice(at, 1)[0]
      }
      return prev.map((r) => {
        if (r.isNew) {
          const c = take(r.sequence)
          return c ? { ...r, lineItemId: c.id, rowVersion: c.row_version, isNew: false } : r
        }
        const c = stored.find((x) => x.id === r.lineItemId)
        return c ? { ...r, rowVersion: c.row_version } : r
      })
    })
  }

  /** Turn a stored line back into editable row state (seed + post-save reseed). */
  function lineItemToRow(c: InvoiceLineItem): LineItemState {
    return {
      lineItemId: c.id,
      rowVersion: c.row_version,
      sequence: c.line_number,
      description: c.description ?? '',
      quantity: c.quantity != null ? String(c.quantity) : '',
      price: c.unit_price != null ? money2(String(c.unit_price)) : '',
      total: c.total != null ? money2(String(c.total)) : '',
      expenseTypeCode: c.expense_type_code || undefined,
      expenseTypeSource: c.expense_type_source || undefined,
      printedCode: c.printed_account_code || undefined,
      companyId: c.company_id || undefined,
      companyName: c.company_name || undefined,
      companyTouched: !!c.company_id && c.company_id !== (header.companyId || undefined),
    }
  }

  /** Turn a refusal into something the reviewer can act on. The API's message is
      human text, so most cases pass it through; the two release gates and a
      concurrent edit each get wording that says what to do next. */
  function reportSaveError(e: unknown, verb: 'Save' | 'Release') {
    if (isApiError(e)) {
      if (e.code === 'unresolved_flags') {
        const types = (e.details?.flag_types as string[] | undefined) ?? []
        setError(
          types.length > 0
            ? `Not released — ${types.length === 1 ? 'this flag is' : 'these flags are'} still unresolved: ${types
                .map(flagLabel)
                .join(', ')}. Nothing was saved.`
            : `${e.message} Nothing was saved.`,
        )
        return
      }
      if (e.code === 'amounts_do_not_reconcile') {
        setError(`Not released — ${e.message} Nothing was saved.`)
        return
      }
      if (e.code === 'version_conflict') {
        // Someone else changed this invoice since it was opened. Show what is
        // actually stored rather than retrying blind over their edit.
        const current = e.details?.current as Invoice | undefined
        if (current) {
          setServerRow(current)
          setRowVersion(current.row_version)
        }
        setError(
          'Someone else changed this invoice while you were editing it, so nothing was saved. Their version is shown below the message; re-apply anything still needed and save again.',
        )
        return
      }
      setError(`${verb} failed: ${e.message}`)
      return
    }
    setError(`${verb} failed: ${errorMessage(e)}`)
  }

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const { raced } = await persistAll(false)
      if (raced) {
        setError('Saved — but you changed the invoice while it was saving, so those later edits are not saved yet. Save again.')
      }
      onChanged()
    } catch (e) {
      reportSaveError(e, 'Save')
    } finally {
      setSaving(false)
    }
  }

  async function handleResolveExplicit(flag: Flag) {
    if (resolving) return // guard against a double-click racing two writes
    setResolving(true)
    setError(null)
    try {
      await resolveFlag(invoice.id, flag.id)
      onChanged()
    } catch (e) {
      setError(`Could not mark resolved: ${errorMessage(e)}`)
    } finally {
      setResolving(false)
    }
  }

  // Save and release in one click — and in one request: the server saves, clears
  // the flags whose condition now holds, checks the release gates and writes the
  // Import Control approval, or refuses and stores nothing.
  async function handleSaveAndRelease() {
    setReleasing(true)
    setError(null)
    try {
      const { released, raced } = await persistAll(true)
      if (raced) {
        setError('Not released — you changed the invoice while it was saving. Those edits are not stored yet: save again, then release.')
        onChanged()
        return
      }
      if (!released) {
        setError('Saved, but not released. Check the flags and the amounts, then release again.')
        onChanged()
        return
      }
      onReleased()
    } catch (e) {
      reportSaveError(e, 'Release')
    } finally {
      setReleasing(false)
    }
  }

  // Archive a record that isn't a payable invoice (statement, cover/routing
  // page, junk scan). One decision; the API checks Import Control authority and
  // moves the invoice to the terminal 'Archived Not An Invoice'.
  async function handleNotAnInvoice(reason: string, category?: string) {
    setArchiving(true)
    setError(null)
    try {
      await createDecision({
        invoice_id: invoice.id,
        step: DecisionStep.ImportControl,
        decision: DecisionOutcome.ArchivedNotAnInvoice,
        reason,
        reason_category: (category as ReasonCategory) || undefined,
      })
      setNotInvoiceOpen(false)
      ;(onArchived ?? onReleased)()
    } catch (e) {
      setError(`Archive failed: ${errorMessage(e)}`)
    } finally {
      setArchiving(false)
    }
  }

  // Why release is blocked (shown on the action bar).
  let releaseHint: string
  if (blockingFlags === 1) releaseHint = 'Resolve the remaining flag to release.'
  else if (blockingFlags > 1) releaseHint = `Resolve all ${blockingFlags} flags to release.`
  else if (rows.length === 0) releaseHint = 'Add at least one line item to release.'
  else if (!header.vendorId || !companyId) releaseHint = 'Set vendor and company to release.'
  else if (linesWithoutExpenseType.length > 0)
    // Named here rather than left to Export Control, where it aborts the whole
    // batch for everyone (QA Q12).
    releaseHint =
      linesWithoutExpenseType.length === 1
        ? `Line ${linesWithoutExpenseType[0].sequence ?? '?'} has no expense type — every line item needs one to release.`
        : `${linesWithoutExpenseType.length} line items have no expense type (${linesWithoutExpenseType
            .map((r) => r.sequence ?? '?')
            .join(', ')}) — every line needs one to release.`
  else if (!headerAddsUp) releaseHint = 'Total must equal subtotal + tax to release.'
  else if (lineItemsCapped && !lineItemsMatchTotal)
    releaseHint =
      'Only the first 40 line items could be read off this document, so the stored lines do not add up to the total. Check the source and roll the line items up (or add what is missing) until they equal the total, then resolve the “Maximum Number Of Line Items Extracted” flag.'
  else if (!lineItemsMatchTotal)
    releaseHint =
      'Line items must add up to the total to release. Sales tax goes inside the line items — add each line’s share of the tax to its amount.'
  else if (companyResolution.outcome === 'mixed')
    releaseHint = `Line items are pointed at ${listCompanies(companyResolution.lineCompanyNames)}, so this invoice is billed to ${PCS_COMPANY_NAME} — releasing sends it to Manual Entry to be keyed by hand, not to the CSV export. Point every line at one company to send it through the export instead.`
  else if (companyResolution.outcome === 'lines')
    releaseHint = `All flags have been resolved — this invoice is ready for release under ${companyName}, the company every line item is pointed at.`
  else releaseHint = 'All flags have been resolved — this invoice is ready for release.'

  return (
    <div className={s.root} ref={rootRef}>
      {error && (
        <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>
      )}

      {/* What is actually stored, after someone else's edit collided with this
          one. Shown rather than merged: only the reviewer can tell which of the
          two values is right. */}
      {serverRow && (
        <div className={s.conflict}>
          <Caption1 className={s.conflictHead}>Currently stored</Caption1>
          <Caption1>Company: {serverRow.company_name ?? '—'} · Vendor: {serverRow.vendor_name ?? '—'}</Caption1>
          <Caption1>
            External ID: {serverRow.external_invoice_id ?? '—'} · Invoice date: {formatNumericDate(serverRow.invoice_date)}
          </Caption1>
          <Caption1>
            Subtotal {serverRow.subtotal ?? '—'} · Tax {serverRow.tax ?? '—'} · Total {serverRow.total ?? '—'}
          </Caption1>
        </div>
      )}

      <div className={s.actionBar}>
        {/* Subtitle2 so the status reads at the same weight as the section headings. */}
        <Subtitle2 className={mergeClasses(s.actionStatus, canSaveAndRelease ? s.actionOk : s.actionBlocked)}>
          {releaseHint}
        </Subtitle2>
        <Button appearance="secondary" onClick={() => setNotInvoiceOpen(true)} disabled={saving || releasing || archiving}>
          Not an Invoice
        </Button>
        <Button appearance="secondary" onClick={handleSave} disabled={saving || releasing || archiving || !dirty}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        <Button appearance="primary" onClick={handleSaveAndRelease} disabled={!canSaveAndRelease || saving || releasing || archiving}>
          {releasing ? 'Releasing…' : 'Save & Release'}
        </Button>
        {onClose && (
          <Button appearance="subtle" icon={<DismissRegular />} aria-label="Close" onClick={onClose} />
        )}
      </div>

      <NotAnInvoiceDialog
        open={notInvoiceOpen}
        submitting={archiving}
        onArchive={handleNotAnInvoice}
        onCancel={() => setNotInvoiceOpen(false)}
      />

      {/* Count the flags that still block (explicit not-yet-resolved + auto whose
          condition doesn't hold), so this heading agrees with the release banner
          instead of showing "N unresolved" while the banner says "all resolved". */}
      <Subtitle2>Flags ({blockingFlags} unresolved)</Subtitle2>
      {flags.length === 0 ? (
        <Body1 className={s.flagNone}>No flags on this invoice.</Body1>
      ) : (
        <div className={s.flagList}>
          {flags.map((f) => {
            const persistedResolved = !!f.resolved_at
            // An auto flag whose condition already holds will clear on the next
            // save — render it as resolved so the list matches the release banner.
            const autoWillClear =
              !persistedResolved &&
              flagResolution(f.flag_type) === 'auto' &&
              !!f.flag_type &&
              isAutoSatisfied(f.flag_type, header, rows, companyId)
            const resolved = persistedResolved || autoWillClear
            const explicit = flagResolution(f.flag_type) === 'explicit'
            return (
              <MessageBar key={f.id} intent={resolved ? 'success' : 'warning'}>
                <MessageBarBody>
                  <MessageBarTitle>{flagLabel(f.flag_type)}</MessageBarTitle>
                  {persistedResolved
                    ? `Resolved by ${f.resolved_by_name ?? 'user'}.`
                    : autoWillClear
                    ? 'Ready — clears on save.'
                    : // Show the human-readable help text, then append the flow's
                      // detail only when it adds context (a matched company,
                      // duplicate invoice #, GL code…). A bare numeric detail is an
                      // internal count, not user-facing — appending "— 1" only
                      // confused reviewers (user note 2026-07-14). Earlier this
                      // field could even replace the whole description; now it's
                      // strictly supplementary.
                      `${flagDescription(f.flag_type)}${f.detail && /\D/.test(f.detail) ? ` — ${f.detail}` : ''}`}
                </MessageBarBody>
                {!resolved && explicit && (
                  <MessageBarActions>
                    <Button size="small" disabled={resolving} onClick={() => handleResolveExplicit(f)}>
                      Mark resolved
                    </Button>
                  </MessageBarActions>
                )}
              </MessageBar>
            )
          })}
        </div>
      )}

      <div className={s.blueRule} />
      <div className={s.topRow} style={pdf ? undefined : { display: 'block' }}>
        {pdf && <div className={s.pdfWrap}>{pdf}</div>}
        <div className={s.headerCol}>
          <Subtitle2>Invoice Summary</Subtitle2>
          <div className={s.grid}>
            {/* The line items set the invoice's company when they are
                unanimous on another one, or disagree (user rule 2026-07-31 —
                resolveInvoiceCompany in lib/export.ts). The field shows what
                will be stored, says what set it, and locks while the lines are
                driving it: the way to change it then is to repoint a line. */}
            <Field
              label="Company"
              validationState={companyErr ? 'error' : 'none'}
              hint={
                companyResolution.outcome === 'mixed'
                  ? `Set by the line items: lines are pointed at ${listCompanies(companyResolution.lineCompanyNames)}, so this invoice goes to ${PCS_COMPANY_NAME} for approval and is keyed into Point Click Care by hand.`
                  : companyResolution.outcome === 'lines'
                  ? `Set by the line items: every line is pointed at ${companyName}.`
                  : undefined
              }
            >
              <EntityPicker
                value={{ id: companyId, name: companyName }}
                disabled={linesSetCompany}
                placeholder="Search companies…"
                search={(t) =>
                  searchCompanies(t).then<EntityOption[]>((cs) =>
                    cs.map((c) => ({ id: c.company_id ?? '', primary: c.company_name ?? '(unnamed)' })),
                  )
                }
                onSelect={(o) => patchHeader({ companyId: o.id, companyName: o.primary })}
              />
            </Field>
            <Field label="Vendor" validationState={vendorErr ? 'error' : 'none'}>
              <EntityPicker
                value={{ id: header.vendorId, name: header.vendorName }}
                placeholder="Search vendors…"
                search={(t) =>
                  searchVendors(t).then<EntityOption[]>((vs) =>
                    vs.map((v) => ({
                      id: v.vendor_id ?? '',
                      primary: v.vendor_name ?? '(unnamed)',
                      secondary: [v.city, v.state_or_province].filter(Boolean).join(', '),
                    })),
                  )
                }
                onSelect={(o) => patchHeader({ vendorId: o.id, vendorName: o.primary })}
              />
            </Field>
            <Field label="Internal ID">
              <Input value={invoice.id ?? '—'} readOnly appearance="filled-darker" />
            </Field>
            <Field label="External ID" validationState={extIdErr ? 'error' : 'none'}>
              <Input value={header.externalinvoiceid} onChange={(_, d) => setH('externalinvoiceid', d.value)} />
            </Field>
            <Field label="Invoice Date">
              <Input type="date" value={header.date} onChange={(_, d) => setH('date', d.value)} />
            </Field>
            <Field label="Received Date">
              <Input value={formatNumericDate(invoice.received_at)} readOnly appearance="filled-darker" />
            </Field>
            <Field label="Subtotal" validationState={totalErr ? 'error' : 'none'}>
              <Input type="number" contentBefore="$" value={header.subtotal} onChange={(_, d) => setH('subtotal', d.value)} onBlur={() => { const f = money2(header.subtotal); if (f !== header.subtotal) setH('subtotal', f) }} />
            </Field>
            <Field label="Tax">
              <Input type="number" contentBefore="$" value={header.tax} onChange={(_, d) => setH('tax', d.value)} onBlur={() => { const f = money2(header.tax); if (f !== header.tax) setH('tax', f) }} />
            </Field>
            <Field label="Total" validationState={totalErr ? 'error' : 'none'}>
              <Input type="number" contentBefore="$" value={header.total} onChange={(_, d) => setH('total', d.value)} onBlur={() => { const f = money2(header.total); if (f !== header.total) setH('total', f) }} />
            </Field>
            <Field label="Currency" validationState={currencyErr ? 'error' : 'none'}>
              <Input value={header.currency} onChange={(_, d) => setH('currency', d.value)} />
            </Field>
            {/* Manual-entry mark (app.md §9 Manual Entry): persisted with Save;
                past the last staffed approval step, the broker routes marked
                invoices to Manual Entry instead of Export Control's CSV path. A labeled
                dropdown so it reads like the other fields (user note,
                2026-07-12); default is always Export / Import. A mixed-company
                invoice is Manual Entry by rule (2026-07-31) — the picker shows
                that and locks. A capped invoice is NOT: it is flagged and
                reviewed like any other (flags.md →
                MaximumLineItemsExtracted). */}
            <Field
              label="Path to Point Click Care"
              className={s.fullRow}
              hint={
                pathLocked
                  ? `Set by the line items: lines are pointed at ${listCompanies(companyResolution.lineCompanyNames)}, so this invoice is keyed into Point Click Care by hand.`
                  : undefined
              }
            >
              {/* inlinePopup keeps the listbox inside the dialog's focus trap —
                  portaled to body it loses focus to the trap and instantly
                  closes (user-reported flicker, 2026-07-12). */}
              <Dropdown
                inlinePopup
                disabled={pathLocked}
                value={manualEntryEffective ? 'Manual Entry' : 'Export / Import'}
                selectedOptions={[manualEntryEffective ? 'manual' : 'csv']}
                onOptionSelect={(_, d) => setH('manualEntry', d.optionValue === 'manual')}
              >
                <Option value="csv">Export / Import</Option>
                <Option value="manual">Manual Entry</Option>
              </Dropdown>
            </Field>
            {/* AI notes are Import Control's concern only — keep them off any
                future read-only reuse of this modal at later steps. When both
                note fields are present the extraction/matching distinction is
                kept; a single note is just "AI notes". */}
            {invoice.status === InvoiceStatus.PendingImportControl &&
              (invoice.ai_extracting_notes || invoice.ai_matching_notes) && (
                <div className={s.aiNotes}>
                  {invoice.ai_extracting_notes && (
                    <Caption1>
                      {invoice.ai_matching_notes ? 'AI extraction notes' : 'AI notes'}: {invoice.ai_extracting_notes}
                    </Caption1>
                  )}
                  {invoice.ai_matching_notes && (
                    <Caption1>
                      {invoice.ai_extracting_notes ? 'AI matching notes' : 'AI notes'}: {invoice.ai_matching_notes}
                    </Caption1>
                  )}
                </div>
              )}
          </div>
        </div>
      </div>

      <Divider />
      <Subtitle2>Line Items Summary</Subtitle2>
      <div className={s.tableScroll}>
      <Table size="small" aria-label="Editable line items" className={s.lineItemsTable}>
        <TableHeader>
          <TableRow>
            {/* Sized columns total 710px; Description takes what's left, with a
                230px floor from the table's own minWidth (QA Q17). */}
            <TableHeaderCell style={{ width: 30 }}>#</TableHeaderCell>
            <TableHeaderCell style={{ width: 150 }}>Company</TableHeaderCell>
            <TableHeaderCell>Description</TableHeaderCell>
            <TableHeaderCell style={{ width: 190 }}>Account</TableHeaderCell>
            <TableHeaderCell style={{ width: 96 }}>Price</TableHeaderCell>
            <TableHeaderCell style={{ width: 76 }}>Quantity</TableHeaderCell>
            <TableHeaderCell style={{ width: 104 }}>Total</TableHeaderCell>
            <TableHeaderCell style={{ width: 64 }} aria-label="Line actions" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.lineItemId}>
              <TableCell>{r.sequence ?? '—'}</TableCell>
              <TableCell>
                {/* Per-line company leads the data columns (planning §4).
                    Untouched lines show (and save as) the invoice's own
                    company; picking one pins the line — exported into that
                    company's PCC file, and it moves the invoice's own company
                    too (the Company field above says how). */}
                <EntityPicker
                  size="small"
                  value={
                    r.companyTouched
                      ? { id: r.companyId, name: r.companyName }
                      : { id: header.companyId, name: header.companyName }
                  }
                  placeholder="Inherits invoice company"
                  search={(t) =>
                    searchCompanies(t).then<EntityOption[]>((cs) =>
                      cs.map((c) => ({ id: c.company_id ?? '', primary: c.company_name ?? '(unnamed)' })),
                    )
                  }
                  onSelect={(o) => setRow(r.lineItemId, { companyId: o.id, companyName: o.primary, companyTouched: true })}
                />
              </TableCell>
              <TableCell>
                <Input className={s.cellInput} size="small" value={r.description} onChange={(_, d) => setRow(r.lineItemId, { description: d.value })} />
              </TableCell>
              {/* Any line without an account is tinted, flag row or not — an
                  added line has no flag but still blocks release (QA Q12). */}
              {/* Any line without an EXPENSE TYPE is tinted — never a line
                  merely awaiting its cost center, which is normal here and is
                  nobody at this step's business. */}
              <TableCell className={!r.expenseTypeCode ? s.cellFlagged : undefined}>
                {/* "What is it?" + its "how was this chosen" info popover, kept
                    together since the provenance is GL-specific (user note
                    2026-07-14 — moved here from the row actions cell). The
                    popover names the EXPENSE TYPE's provenance only: this is
                    the one step forbidden from showing the cost center. */}
                <div className={s.accountCell}>
                  <div className={s.accountPicker}>
                    <EntityPicker
                      size="small"
                      // `expenseTypeLabel` is set only by a pick in this session
                      // — a line loaded from the server carries the CODE and
                      // nothing else, because the wire has no label field. Falling
                      // back to the master lookup is what stops an already-coded
                      // line rendering an EMPTY box: not tinted, not blocking
                      // release, and reading as uncoded while it is in fact fine.
                      value={{
                        id: r.expenseTypeCode,
                        name: r.expenseTypeLabel ?? labels.expenseType(r.expenseTypeCode),
                      }}
                      placeholder="What is it?"
                      search={(t) =>
                        searchExpenseTypes(t).then<EntityOption[]>((es) =>
                          es.map((e) => ({
                            id: e.expense_type_code,
                            primary: e.label,
                            secondary: e.expense_type_code,
                          })),
                        )
                      }
                      onSelect={(o) =>
                        setRow(r.lineItemId, {
                          expenseTypeCode: o.id,
                          expenseTypeLabel: o.primary,
                          expenseTypeSource: 'Manual',
                        })
                      }
                    />
                  </div>
                  {(r.expenseTypeSource || r.printedCode) && (
                    <Popover withArrow>
                      <PopoverTrigger disableButtonEnhancement>
                        <Button appearance="subtle" size="small" icon={<InfoRegular />} aria-label="How this expense type was chosen" title="How this expense type was chosen" />
                      </PopoverTrigger>
                      <PopoverSurface>
                        <div className={s.provenance}>
                          <Caption1 className={s.provenanceHead}>How this expense type was chosen</Caption1>
                          {r.expenseTypeSource && <Caption1>Source: {accountSourceLabel(r.expenseTypeSource)}</Caption1>}
                          {r.printedCode && <Caption1>Printed code on the document: {r.printedCode}</Caption1>}
                        </div>
                      </PopoverSurface>
                    </Popover>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <Input className={s.cellInput} size="small" type="number" contentBefore="$" value={r.price} onChange={(_, d) => setRow(r.lineItemId, { price: d.value })} onBlur={() => { const f = money2(r.price); if (f !== r.price) setRow(r.lineItemId, { price: f }) }} />
              </TableCell>
              <TableCell>
                <Input className={s.cellInput} size="small" type="number" value={r.quantity} onChange={(_, d) => setRow(r.lineItemId, { quantity: d.value })} />
              </TableCell>
              <TableCell>
                <Input className={s.cellInput} size="small" type="number" contentBefore="$" value={r.total} onChange={(_, d) => setRow(r.lineItemId, { total: d.value })} onBlur={() => { const f = money2(r.total); if (f !== r.total) setRow(r.lineItemId, { total: f }) }} />
              </TableCell>
              <TableCell>
                <div className={s.rowActions}>
                  {/* Per-line entity tagging (AE4) lives in a popover off this
                      button — it used to render open under every line item row,
                      which cluttered the table (user note 2026-07-14). Only saved
                      lines have a real id to hang entity rows on; a
                      just-added line shows a hint until it's saved. */}
                  <Popover mountNode={dialogEl ?? undefined}>
                    <PopoverTrigger disableButtonEnhancement>
                      <Button appearance="subtle" size="small" icon={<LinkRegular />} aria-label="Entities this line is for" title="Entities this line is for" />
                    </PopoverTrigger>
                    <PopoverSurface className={s.entityPopSurface}>
                      <div className={s.entityPop}>
                        {r.isNew ? (
                          <Caption1 className={s.entityHint}>Save the invoice to tag entities on this line.</Caption1>
                        ) : (
                          <AssociatedEntitiesEditor
                            invoice={invoice}
                            lineItem={lineItemsById.get(r.lineItemId) ?? ({ id: r.lineItemId } as InvoiceLineItem)}
                          />
                        )}
                      </div>
                    </PopoverSurface>
                  </Popover>
                  <Button appearance="subtle" size="small" icon={<DeleteRegular />} aria-label="Remove line item" onClick={() => removeRow(r.lineItemId)} />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
      <div className={s.addRow}>
        <Button appearance="subtle" size="small" icon={<AddRegular />} onClick={addRow}>
          Add line item
        </Button>
      </div>

      {/* Accounts Summary (planning §4) — grouped by EXPENSE TYPE here, because
          Import Control never shows a cost center and a ChartImplied line
          already carries both halves. Dynamic: recomputes from the same
          in-memory row state the table edits, so per-account totals track as the
          reviewer swaps a cost center. Effective per-line company: a pinned line
          keeps its own, an untouched line follows the header. The record's
          company is the resolved one, so the per-company breakdown appears
          exactly when the lines are not all on the invoice's company. */}
      <AccountsSummary
        recordCompanyId={companyId}
        groupBy="expense_type"
        rows={rows.map((r) => ({
          expenseTypeCode: r.expenseTypeCode,
          // Same fallback as the picker above: without it the summary degraded to
          // raw codes for every line the reviewer had not re-picked this session,
          // which is the opposite of "the Facility Manager reads words".
          expenseTypeLabel: r.expenseTypeLabel ?? labels.expenseType(r.expenseTypeCode),
          companyId: r.companyTouched ? r.companyId : header.companyId,
          companyName: r.companyTouched ? r.companyName : header.companyName,
          total: toNumber(r.total),
        }))}
      />
    </div>
  )
}
